import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import FieldErrors from 'Components/FieldErrors'
import type { ErrorFields } from 'Components/FieldErrors/FieldErrors'
import MultiSelect, { type OptionType } from 'Components/MultiSelect/MultiSelect'
import StyledLabel from 'Components/StyledLabel'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import {
  type CampaignDetailShape,
  DiscountType,
  fetchCampaignProductList
} from 'Services/SalesManagement/Features/Campaign/campaignSlice'
import useTypedSelector, { useAppDispatch } from 'Store'
import { BACKEND_DATE_TIME_FORMAT } from 'Util/constants'
import { dateFormatter } from 'Util/formatters'
import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  TextField
} from '@mui/material'
import Box from '@mui/material/Box'
import { type FC, type default as React, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { c, t } from 'ttag'

type CampaignDetailErrors = ErrorFields<CampaignDetailShape>

const CampaignDetails: FC<{
  campaignDetails: CampaignDetailShape
  fieldErrors: CampaignDetailErrors
  handleSetDetails: (
    value: string | number | string[] | Date,
    name: keyof CampaignDetailShape
  ) => void
  children?: React.ReactNode
}> = ({ campaignDetails, fieldErrors, handleSetDetails, children }) => {
  const dispatch = useAppDispatch()
  const { authorities } = useAuthorityContext()

  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)

  const { campaignProducts, campaignProductsLoading } = useTypedSelector(state => state.campaign)

  const handleSelectChange = (selected: string[], name: keyof CampaignDetailShape) => {
    handleSetDetails(selected, name)
  }

  const handleDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value, BACKEND_DATE_TIME_FORMAT).toString()
    }
    handleSetDetails(formattedValue, name as keyof CampaignDetailShape)
  }

  const handleCampaignNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    handleSetDetails(value, 'name')
  }

  const handleSalesChannelChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target
    handleSetDetails(value, 'sales_channel_group')

    dispatch(fetchCampaignProductList({ salesChannelGroup: parseInt(value) }))
  }

  const handleDiscountTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    handleSetDetails(parseInt(value), 'discount_type')
  }

  const handleDiscountAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    handleSetDetails(parseFloat(value), 'discount_amount')
  }

  useEffect(() => {
    if (instanceIdParam && campaignDetails.sales_channel_group) {
      dispatch(fetchCampaignProductList({ salesChannelGroup: campaignDetails.sales_channel_group }))
    }
  }, [dispatch, instanceIdParam, campaignDetails.sales_channel_group])

  const SALES_CHANNEL_OPTIONS = {
    1: t`Waltti webshop`,
    2: t`Mobile`
  }

  const authorityOptions: OptionType[] =
    authorities?.map(authority => [authority.code, authority.name]) ?? []

  const isCampaignProductLoading = campaignProductsLoading === 'pending'

  return (
    <>
      <Box>
        <TextField
          sx={{ minWidth: 300 }}
          required
          label={t`Campaign name`}
          value={campaignDetails.name}
          onChange={handleCampaignNameChange}
          error={fieldErrors.name?.length > 0}
          helperText={<FieldErrors error={fieldErrors.name} identifier="name" />}
        />
      </Box>
      <Box sx={{ minWidth: 200, maxWidth: 400 }}>
        <MultiSelect
          handleChange={selected => handleSelectChange(selected, 'authorities')}
          label={t`Authority`}
          required={true}
          options={authorityOptions}
          initialValues={campaignDetails.authorities.map(authority => [authority, authority])}
        />
      </Box>
      <Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <CustomDateTimePicker
            name="start_date"
            value={campaignDetails.start_date ? new Date(campaignDetails.start_date) : null}
            dateToCompare={campaignDetails.end_date ? new Date(campaignDetails.end_date) : null}
            dateToCompareFieldName="end_date"
            isStartDate={true}
            onChange={handleDateTimePickerChange}
            label={t`Active from`}
            isError={fieldErrors.start_date?.length > 0}
            isRequired={true}
          >
            <FieldErrors error={fieldErrors.start_date} identifier="start_date" />
          </CustomDateTimePicker>

          <Box>{` - `}</Box>
          <CustomDateTimePicker
            name="end_date"
            value={campaignDetails.end_date ? new Date(campaignDetails.end_date) : null}
            dateToCompare={campaignDetails.start_date ? new Date(campaignDetails.start_date) : null}
            dateToCompareFieldName="start_date"
            isStartDate={false}
            onChange={handleDateTimePickerChange}
            label={t`Active to`}
            isError={fieldErrors.end_date?.length > 0}
            isRequired={true}
          >
            <FieldErrors error={fieldErrors.end_date} identifier="end_date" />
          </CustomDateTimePicker>
        </Box>
      </Box>
      <Box>
        <FormControl sx={{ minWidth: 200, maxWidth: 400 }}>
          <InputLabel id="sales-channel-selector-label">{t`Sales channel`}</InputLabel>
          <Select
            labelId="sales-channel-selector-label"
            label={t`Sales channel`}
            value={campaignDetails.sales_channel_group?.toString() || ''}
            onChange={handleSalesChannelChange}
            disabled={Boolean(instanceIdParam)}
          >
            {Object.entries(SALES_CHANNEL_OPTIONS).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {campaignDetails.sales_channel_group && (
        <Box sx={{ minWidth: 200, maxWidth: 400 }}>
          <MultiSelect
            isLoading={isCampaignProductLoading}
            handleChange={selected => handleSelectChange(selected, 'product_codes')}
            label={t`Products`}
            required={true}
            options={campaignProducts.map(product => [product[0], `${product[1]} (${product[0]})`])}
            initialValues={campaignDetails.product_codes.map(productCode => [
              productCode,
              productCode
            ])}
          />
        </Box>
      )}
      <Box>
        <StyledLabel>{t`Discount type`}</StyledLabel>
        <FormControl error={fieldErrors.discount_type?.length > 0}>
          <RadioGroup
            name="radio-buttons-group"
            value={campaignDetails.discount_type}
            onChange={handleDiscountTypeChange}
          >
            <FormControlLabel
              value={DiscountType.percent}
              control={<Radio size="small" />}
              label={t`Percent`}
            />
            <FormControlLabel
              value={DiscountType.value}
              control={<Radio size="small" />}
              label={c('discount').t`Amount`}
            />
          </RadioGroup>
          <FieldErrors error={fieldErrors.discount_type} identifier="discount_type" />
        </FormControl>
        <Box>
          <TextField
            sx={{ minWidth: 200 }}
            required
            type="number"
            size="small"
            label={
              campaignDetails.discount_type === DiscountType.percent
                ? t`Percent`
                : t`Amount in euros`
            }
            value={campaignDetails.discount_amount}
            onChange={handleDiscountAmountChange}
            error={fieldErrors.discount_amount?.length > 0}
            helperText={
              <FieldErrors error={fieldErrors.discount_amount} identifier="discount_amount" />
            }
          />
        </Box>
      </Box>
      {children}
    </>
  )
}

export { CampaignDetails }
